import React from 'react';
import Skeleton from 'react-loading-skeleton';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, ReferenceLine
} from 'recharts';
import AppointmentLineChart from './AppointmentLineChart';
import { formatNumber, interpolate } from './shared/chartFormat';

// Fixed chart greens, same family as STATUS_COLORS.completed.
const BAR_COLOR = '#5eb767';
const WEEKEND_COLOR = '#bcdfc0';
const PEAK_COLOR = '#2c6e38';
const MEAN_COLOR = '#f0a63a';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div style={{
      background: 'rgba(17, 28, 48, 0.94)',
      border: '1px solid rgba(110, 137, 178, 0.45)',
      borderRadius: 10,
      padding: '9px 12px',
      color: '#d8e4f8',
      fontSize: 12
    }}>
      <strong style={{ color: '#9fd8a6' }}>{label}</strong>: {payload[0].value}
    </div>
  );
};

// Rounded pill rendered as a ReferenceLine label — used for the mean tag on
// the right and the peak-day annotation on top of its bar.
const PillLabel = ({ viewBox, text, background, align }) => {
  if (!viewBox) return null;
  const width = text.length * 6.4 + 18;
  const x = align === 'right'
    ? viewBox.x + (viewBox.width || 0) - width - 4
    : viewBox.x - width / 2;
  const y = align === 'right' ? viewBox.y - 10 : (viewBox.y || 0) + 2;

  return (
    <g>
      <rect x={x} y={y} rx={10} ry={10} width={width} height={20} fill={background} opacity={0.95} />
      <text x={x + width / 2} y={y + 14} textAnchor="middle" fontSize={11} fontWeight={700} fill="#ffffff">
        {text}
      </text>
    </g>
  );
};

// PE-438: daily volume for the statistics dashboard. One /charts response
// feeds the bars, the mean line, the peak pill and the insight strip; the
// Bulanan toggle swaps in the existing monthly line chart.
export default class AppointmentVolumeChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = { mode: 'daily', rows: [], loading: true, error: false };
  }

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.query !== this.props.query) this.fetchData();
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  fetchData() {
    const requestId = (this.requestId = (this.requestId || 0) + 1);
    this.setState({ loading: true, error: false });

    fetch('/api/appointments/charts' + (this.props.query || ''))
      .then(res => (res.ok ? res.json() : Promise.reject(res)))
      .then(data => {
        if (this.unmounted || requestId !== this.requestId) return;
        const rows = Object.entries(data || {}).map(([day, value]) => {
          const date = new Date(`${day}T00:00:00`);
          return { day, value: Number(value) || 0, dow: date.getDay(), dom: date.getDate(), month: date.getMonth() };
        }).sort((a, b) => (a.day < b.day ? -1 : 1));
        this.setState({ rows, loading: false });
      })
      .catch(() => {
        if (this.unmounted || requestId !== this.requestId) return;
        this.setState({ rows: [], loading: false, error: true });
      });
  }

  // Mean, peak, per-weekday averages and deviation — all client-side, all
  // from the one response already fetched.
  stats() {
    const { rows } = this.state;
    const mean = rows.reduce((sum, row) => sum + row.value, 0) / rows.length;
    const peak = rows.reduce((max, row) => (row.value > max.value ? row : max), rows[0]);

    const sums = {};
    const counts = {};
    rows.forEach(row => {
      sums[row.dow] = (sums[row.dow] || 0) + row.value;
      counts[row.dow] = (counts[row.dow] || 0) + 1;
    });
    const weekdayAvgs = Object.keys(sums).map(dow => ({ dow: Number(dow), avg: sums[dow] / counts[dow] }));
    const busiest = weekdayAvgs.reduce((max, day) => (day.avg > max.avg ? day : max), weekdayAvgs[0]);
    const quietest = weekdayAvgs.reduce((min, day) => (day.avg < min.avg ? day : min), weekdayAvgs[0]);

    const variance = rows.reduce((sum, row) => sum + ((row.value - mean) ** 2), 0) / rows.length;
    const deviationPct = mean > 0 ? (Math.sqrt(variance) / mean) * 100 : 0;

    return { mean, peak, busiest, quietest, deviationPct };
  }

  renderInsights({ mean, busiest, quietest, deviationPct }) {
    const { labels, locale } = this.props;
    const weekdays = labels.weekdays_long.split(',');
    const pct = day => (mean > 0 ? Math.abs((day.avg - mean) / mean) * 100 : 0);
    const items = [
      {
        icon: 'zmdi-trending-up',
        label: labels.insight_busiest,
        value: weekdays[busiest.dow],
        caption: `${interpolate(labels.insight_avg, { value: formatNumber(Math.round(busiest.avg), locale) })} · ${interpolate(labels.insight_above, { value: formatNumber(pct(busiest), locale, { maximumFractionDigits: 0 }) })}`
      },
      {
        icon: 'zmdi-trending-down',
        label: labels.insight_quietest,
        value: weekdays[quietest.dow],
        caption: `${interpolate(labels.insight_avg, { value: formatNumber(Math.round(quietest.avg), locale) })} · ${interpolate(labels.insight_below, { value: formatNumber(pct(quietest), locale, { maximumFractionDigits: 0 }) })}`
      },
      {
        icon: 'zmdi-swap-vertical',
        label: labels.insight_consistency,
        value: `± ${formatNumber(deviationPct, locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`,
        caption: labels.insight_deviation
      }
    ];

    return (
      <div className="appt-stats__insights">
        {items.map(item => (
          <div className="appt-stats__insight" key={item.label}>
            <span className="appt-stats__insight-icon"><i className={`zmdi ${item.icon}`} /></span>
            <div>
              <div className="appt-stats__insight-label">{item.label}</div>
              <div className="appt-stats__insight-value">{item.value}</div>
              <div className="appt-stats__insight-caption">{item.caption}</div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderDaily() {
    const { labels, locale } = this.props;
    const { rows, loading, error } = this.state;

    if (loading) {
      return (
        <div style={{ height: 340, padding: '12px 0' }}>
          <Skeleton height={300} borderRadius={14} />
        </div>
      );
    }
    if (error) return <div className="text-center text-muted py-5">{labels.load_error}</div>;
    if (!rows.length) return <div className="text-center text-muted py-5">{labels.no_data}</div>;

    const months = labels.months_short.split(',');
    const stats = this.stats();
    const meanText = interpolate(labels.mean_label, { value: formatNumber(Math.round(stats.mean), locale) });
    const peakText = interpolate(labels.peak_label, {
      date: `${stats.peak.dom} ${months[stats.peak.month]}`,
      value: formatNumber(stats.peak.value, locale)
    });
    const tickFor = day => {
      const row = rows.find(r => r.day === day);
      if (!row) return day;
      return row.dom === 1 || row.day === rows[0].day || row.day === rows[rows.length - 1].day
        ? `${row.dom} ${months[row.month]}`
        : String(row.dom);
    };

    return (
      <div>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={rows} margin={{ top: 30, right: 6, left: -10, bottom: 2 }}>
            <CartesianGrid strokeDasharray="3 5" vertical={false} stroke="#e7eef8" />
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#7f8ea8', fontWeight: 600 }}
              tickFormatter={tickFor}
              interval="preserveStartEnd"
              minTickGap={18}
            />
            <YAxis
              allowDecimals={false}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#8a98b0' }}
              tickFormatter={value => formatNumber(value, locale)}
              width={44}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(50, 79, 124, 0.08)' }} />
            <Bar dataKey="value" radius={[5, 5, 0, 0]} maxBarSize={26}>
              {rows.map(row => (
                <Cell
                  key={row.day}
                  fill={row.day === stats.peak.day ? PEAK_COLOR : (row.dow === 0 || row.dow === 6 ? WEEKEND_COLOR : BAR_COLOR)}
                />
              ))}
            </Bar>
            <ReferenceLine
              y={stats.mean}
              stroke={MEAN_COLOR}
              strokeDasharray="5 4"
              label={<PillLabel text={meanText} background={MEAN_COLOR} align="right" />}
            />
            <ReferenceLine
              x={stats.peak.day}
              stroke="transparent"
              label={<PillLabel text={peakText} background={PEAK_COLOR} />}
            />
          </BarChart>
        </ResponsiveContainer>
        {this.renderInsights(stats)}
      </div>
    );
  }

  render() {
    const { labels, dateField } = this.props;
    const { mode } = this.state;
    const toggles = [
      { key: 'daily', label: labels.toggle_daily },
      { key: 'monthly', label: labels.toggle_monthly }
    ];

    return (
      <div>
        <div className="appt-stats__card-head">
          <div>
            <h4 className="card-title mb-1">{labels.volume_title}</h4>
            <p className="appt-stats__card-subtitle">
              {dateField === 'date' ? labels.volume_basis_visit : labels.volume_basis_created}
            </p>
          </div>
          <div className="appt-stats__pills" role="group" aria-label={labels.volume_title}>
            {toggles.map(toggle => (
              <button
                key={toggle.key}
                type="button"
                className={`btn btn-sm rounded-pill ${mode === toggle.key ? 'btn-primary' : 'btn-outline-primary'} appt-stats__pill`}
                aria-pressed={mode === toggle.key}
                onClick={() => this.setState({ mode: toggle.key })}
              >
                {toggle.label}
              </button>
            ))}
          </div>
        </div>

        {mode === 'daily' ? this.renderDaily() : (
          <div>
            {/* line_chart follows the hospital filter only — no range, no basis. */}
            <AppointmentLineChart query={this.props.monthlyQuery} />
            <p className="appt-stats__card-note">{labels.monthly_note}</p>
          </div>
        )}
      </div>
    );
  }
}
